import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Box,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';
import { blogService, getLastSource, getLastError } from '../../services/api/blogService';

function formatDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toISOString().slice(0, 10);
  } catch {
    return String(d);
  }
}

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [dataSource, setDataSource] = useState('loading');
  const [lastError, setLastError] = useState(null);

  const refreshData = useCallback(async () => {
    try {
      const list = await blogService.listBlogs({ status: 'published', limit: 100 });
      setBlogs(list);
      setDataSource(getLastSource());
      setLastError(getLastError());
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e && e.message ? e.message : String(e) };
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        await refreshData();
        if (!mounted) return;
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [refreshData]);

  const filteredBlogs = useMemo(() => {
    let list = blogs || [];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(blog =>
        (blog.title || '').toLowerCase().includes(q) ||
        (blog.excerpt || '').toLowerCase().includes(q) ||
        (blog.tags || []).some(t => String(t).toLowerCase().includes(q))
      );
    } else if (selectedTag) {
      list = list.filter(blog => (blog.tags || []).includes(selectedTag));
    }
    return list;
  }, [blogs, searchTerm, selectedTag]);

  const handleTagClick = (tag) => {
    if (selectedTag === tag) {
      setSelectedTag(null);
    } else {
      setSelectedTag(tag);
      setSearchTerm('');
    }
  };

  const allTags = useMemo(() => [...new Set((blogs || []).flatMap(blog => blog.tags || []))], [blogs]);

  return (
    <>
      <Helmet>
        <title>Blog | ScrumTherapist</title>
        <link rel="canonical" href="https://www.scrumtherapist.com/blog" />
        <meta name="description" content="Insights, tips, and best practices to enhance your Agile Scrum journey." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.scrumtherapist.com/blog#webpage",
            "url": "https://www.scrumtherapist.com/blog",
            "name": "Blog",
            "description": "Insights, tips, and best practices to enhance your Agile Scrum journey.",
            "isPartOf": { "@id": "https://www.scrumtherapist.com/#website" }
          })}
        </script>
      </Helmet>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {!loading && dataSource === 'fallback' && (
          <Box
            sx={{
              mb: 4,
              p: 2,
              borderRadius: 2,
              bgcolor: 'warning.lighter',
              border: 1,
              borderColor: 'warning.light',
              color: 'warning.dark'
            }}
          >
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              ⚠️ Showing Offline Sample Data
            </Typography>
            <Typography variant="body2">
              Could not reach the blog backend ({lastError?.status != null ? `HTTP ${lastError.status}` : 'offline'}). New AI articles will not appear until the backend is reachable.
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h1" component="h2" gutterBottom fontWeight="bold" color="primary">
            Blog
          </Typography>
          <Typography variant="h4" color="text.secondary" sx={{ mb: 4 }}>
            Insights, tips, and best practices to enhance your Agile Scrum journey
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'center', mb: 4 }}>
            <TextField
              placeholder="Search articles..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedTag(null);
              }}
              sx={{ width: { xs: '80%', md: '30%' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 4 }}>
            {allTags.map(tag => (
              <Chip
                key={tag}
                label={tag}
                clickable
                color={selectedTag === tag ? "primary" : "default"}
                onClick={() => handleTagClick(tag)}
              />
            ))}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredBlogs.length > 0 ? (
          <Grid container spacing={3} alignItems="stretch">
            {filteredBlogs.map((blog) => (
              <Grid item xs={12} sm={6} md={4} key={blog.id} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4,
                    }
                  }}
                >
                  <CardActionArea component={Link} to={`/blog/${blog.slug || blog.id}`} sx={{ height: '100%' }}>
                    <CardMedia
                      component="img"
                      height="180"
                      width='100%'
                      image={blog.image}
                      alt={blog.title}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h6" component="h2" fontWeight="medium">
                        {blog.title}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {(blog.tags || []).slice(0, 2).map(tag => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                        {(blog.tags || []).length > 2 && (
                          <Chip label={`+${(blog.tags || []).length - 2}`} size="small" variant="outlined" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {blog.excerpt}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(blog.date)} • {blog.author}
                          {blog.aiGenerated && <> · </>}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          Read more
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No articles found matching your search.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setSelectedTag(null);
              }}
            >
              Clear filters
            </Button>
          </Box>
        )}
      </Container>
    </>
  );
};

export default Blog;
